import { Component } from '@theme/component';

const SLIDE_DURATION = 320;

/**
 * A product gallery showing one large stage image with a horizontally scrollable thumbnail
 * strip beneath it. Selecting a thumbnail slides the old stage media out and the new one in.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} stage - The stage that holds the active media.
 * @property {HTMLElement[]} [stageItem] - Every media node rendered into the stage.
 * @property {HTMLButtonElement[]} [thumb] - The thumbnail buttons.
 *
 * @extends {Component<Refs>}
 */
class CasscadoProductGallery extends Component {
  requiredRefs = ['stage'];

  /** @type {number} */
  #activeIndex = 0;

  /** @type {Animation[]} */
  #animations = [];

  connectedCallback() {
    super.connectedCallback();

    const initial = Number(this.dataset.initialIndex ?? 0);
    this.#activeIndex = Number.isNaN(initial) ? 0 : initial;
    this.#apply(this.#activeIndex, { animate: false });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cancelAnimations();
  }

  /**
   * Re-applies the selection after a section re-render, which happens on every variant change.
   * The server puts the variant's media first, so the stage resets to the first item.
   */
  updatedCallback() {
    this.#activeIndex = 0;
    this.#apply(0, { animate: false });
  }

  /**
   * Selects a thumbnail. Bound through `on:click="/select/<index>"`, which delivers the path
   * segment as the first argument, already coerced to a number.
   * @param {unknown} data - The index encoded in the handler path.
   */
  select(data) {
    const index = typeof data === 'number' ? data : Number(data);

    if (Number.isNaN(index) || index === this.#activeIndex) return;

    this.#apply(index, { animate: true, forward: index > this.#activeIndex });
    this.#activeIndex = index;
  }

  /**
   * @param {number} index - The index to show.
   * @param {{animate: boolean, forward?: boolean}} options - Whether to run the slide transition.
   */
  #apply(index, { animate, forward = true }) {
    const items = this.#stageItems;
    const next = items[index];

    if (!next) return;

    // A transition still running owns an outgoing element that is `data-active` but no longer
    // current. Cancelling first, then reading the flag, keeps the two in step.
    this.#cancelAnimations();
    this.#markThumbnails(index);

    const previous = items.find((item) => item !== next && item.hasAttribute('data-active'));

    for (const item of items) {
      if (item !== next && item !== previous) item.removeAttribute('data-active');
    }

    next.setAttribute('data-active', '');

    if (!animate || !previous || this.#prefersReducedMotion) {
      previous?.removeAttribute('data-active');
      this.#pauseVideos(next);
      return;
    }

    const distance = forward ? 1 : -1;
    const timing = { duration: SLIDE_DURATION, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' };

    const outgoing = previous.animate(
      [
        { transform: 'translateX(0)', opacity: 1 },
        { transform: `translateX(${-distance * 100}%)`, opacity: 0 },
      ],
      timing
    );

    const incoming = next.animate(
      [
        { transform: `translateX(${distance * 100}%)`, opacity: 0 },
        { transform: 'translateX(0)', opacity: 1 },
      ],
      timing
    );

    this.#animations = [outgoing, incoming];

    outgoing.finished
      .then(() => previous.removeAttribute('data-active'))
      // A cancelled animation rejects; the next transition then owns the attribute.
      .catch(() => {});

    this.#pauseVideos(next);
  }

  /**
   * Stops any video that is no longer on the stage so audio does not keep playing behind
   * the newly selected media.
   * @param {Element} active - The media node that is now active.
   */
  #pauseVideos(active) {
    for (const video of this.querySelectorAll('video')) {
      if (!active.contains(video)) video.pause();
    }
  }

  /**
   * @param {number} index - The selected index.
   */
  #markThumbnails(index) {
    const thumbs = this.#thumbs;

    for (const [position, thumb] of thumbs.entries()) {
      const selected = position === index;
      thumb.setAttribute('aria-selected', String(selected));
      thumb.toggleAttribute('data-active', selected);
    }

    const active = thumbs[index];
    const strip = active?.parentElement;

    // scrollIntoView would also scroll the page vertically to reach the strip, so the strip's
    // own scroll offset is set directly instead.
    if (active && strip && strip.scrollWidth > strip.clientWidth) {
      const target = active.offsetLeft - (strip.clientWidth - active.clientWidth) / 2;
      strip.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
    }
  }

  #cancelAnimations() {
    const [outgoing] = this.#animations;

    for (const animation of this.#animations) animation.cancel();
    this.#animations = [];

    // `cancel()` rejects the `finished` promise, so the outgoing element would keep the flag.
    if (outgoing?.effect instanceof KeyframeEffect) {
      const target = outgoing.effect.target;
      if (target instanceof HTMLElement) target.removeAttribute('data-active');
    }
  }

  /** @returns {HTMLElement[]} */
  get #stageItems() {
    const { stageItem } = this.refs;
    return Array.isArray(stageItem) ? stageItem : stageItem ? [stageItem] : [];
  }

  /** @returns {HTMLButtonElement[]} */
  get #thumbs() {
    const { thumb } = this.refs;
    return Array.isArray(thumb) ? thumb : thumb ? [thumb] : [];
  }

  get #prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
}

if (!customElements.get('casscado-product-gallery')) {
  customElements.define('casscado-product-gallery', CasscadoProductGallery);
}
